import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type RefObject
} from 'react';
import { ChevronRightIcon, SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export const SEARCH_MENU_WIDTH = 280;
const MENU_GAP = 8;
const VIEWPORT_PAD = 12;

function isDarkTheme(): boolean {
  return (
    document.documentElement.getAttribute('data-bs-theme') === 'dark' ||
    Boolean(document.querySelector('.mcp-chat-page.dark, .aui-root.dark'))
  );
}

/**
 * Fixed position above `anchor`. `align: 'right'` lines up right edges
 * (RTL inline-start); `'left'` lines up left edges.
 */
export function menuStyleAbove(
  anchor: DOMRect,
  {
    width = SEARCH_MENU_WIDTH,
    maxHeightCap = 280,
    align = 'right'
  }: { width?: number; maxHeightCap?: number; align?: 'left' | 'right' } = {}
): CSSProperties {
  const spaceAbove = Math.max(120, anchor.top - VIEWPORT_PAD);
  const preferredLeft = align === 'right' ? anchor.right - width : anchor.left;
  return {
    position: 'fixed',
    bottom: window.innerHeight - anchor.top + MENU_GAP,
    left: Math.min(
      Math.max(VIEWPORT_PAD, preferredLeft),
      window.innerWidth - width - VIEWPORT_PAD
    ),
    width,
    maxHeight: Math.min(
      spaceAbove - MENU_GAP,
      window.innerHeight * 0.6,
      maxHeightCap
    ),
    zIndex: 1080
  };
}

type McpMenuShellProps = {
  style: CSSProperties;
  className?: string;
  ariaLabel: string;
  children: ReactNode;
};

/** Portal-ready popover container shared by the chat composer menus. */
export const McpMenuShell = forwardRef<HTMLDivElement, McpMenuShellProps>(
  ({ style, className, ariaLabel, children }, ref) => (
    <div
      ref={ref}
      role="listbox"
      aria-label={ariaLabel}
      dir="rtl"
      data-theme={isDarkTheme() ? 'dark' : 'light'}
      style={style}
      className={cn(
        'mcp-at-mention-menu mcp-picker-menu',
        'd-flex flex-column border rounded-3 shadow overflow-hidden text-body',
        className
      )}
    >
      {children}
    </div>
  )
);
McpMenuShell.displayName = 'McpMenuShell';

type McpMenuSearchHeaderProps = {
  title: string;
  search: string;
  onSearchChange: (value: string) => void;
  searchRef?: RefObject<HTMLInputElement | null>;
  searchInputProps?: Pick<
    InputHTMLAttributes<HTMLInputElement>,
    'onFocus' | 'onBlur' | 'onKeyDown'
  >;
  searchAriaLabel?: string;
  onBack?: () => void;
};

export const McpMenuSearchHeader: FC<McpMenuSearchHeaderProps> = ({
  title,
  search,
  onSearchChange,
  searchRef,
  searchInputProps,
  searchAriaLabel = 'جستجو',
  onBack
}) => (
  <div className="mcp-at-mention-menu__header d-flex align-items-center gap-1 border-bottom px-2">
    {onBack ? (
      <button
        type="button"
        className="mcp-at-mention-menu__icon-btn d-inline-flex align-items-center justify-content-center border-0 bg-transparent rounded-2 p-0"
        aria-label="بازگشت"
        onMouseDown={e => e.preventDefault()}
        onClick={onBack}
      >
        <ChevronRightIcon size={14} aria-hidden />
      </button>
    ) : null}
    <span className="mcp-at-mention-menu__title text-truncate">{title}</span>
    <label className="mcp-at-mention-menu__search ms-auto mb-0 d-inline-flex align-items-center gap-1 rounded-2 px-2">
      <SearchIcon size={11} className="opacity-70 flex-shrink-0" aria-hidden />
      <input
        ref={searchRef}
        type="text"
        value={search}
        placeholder="جستجو..."
        aria-label={searchAriaLabel}
        autoComplete="off"
        spellCheck={false}
        className="border-0 bg-transparent p-0 outline-none"
        onChange={e => onSearchChange(e.target.value)}
        {...searchInputProps}
      />
    </label>
  </div>
);

export const McpMenuEmpty: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mcp-at-mention-menu__empty d-flex align-items-center gap-2 px-3 py-2 text-600 fs--1">
    <SearchIcon size={14} className="opacity-50" aria-hidden />
    <span>{children}</span>
  </div>
);

type McpMenuItemProps = {
  index: number;
  active: boolean;
  onActiveIndexChange: (index: number) => void;
  onSelect: () => void;
  title?: string;
  className?: string;
  children: ReactNode;
};

export const McpMenuItem: FC<McpMenuItemProps> = ({
  index,
  active,
  onActiveIndexChange,
  onSelect,
  title,
  className,
  children
}) => (
  <li>
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-at-index={index}
      title={title}
      className={cn(
        'mcp-picker-item mcp-at-mention-item d-flex w-100 align-items-center gap-1 border-0 rounded-2 text-start',
        active && 'is-selected',
        className
      )}
      onMouseEnter={() => onActiveIndexChange(index)}
      onMouseDown={e => e.preventDefault()}
      onClick={onSelect}
    >
      {children}
    </button>
  </li>
);

export const McpMenuList: FC<{ children: ReactNode }> = ({ children }) => (
  <ul className="list-unstyled mb-0 p-1 overflow-auto" style={{ minHeight: 0 }}>
    {children}
  </ul>
);

/**
 * Trigger-anchored searchable popover: open/close, outside click, reposition,
 * search filtering and arrow/Enter/Escape navigation.
 */
export function useSearchMenuPopover<T>({
  items,
  filter,
  onPick,
  isSelected
}: {
  items: T[];
  filter: (items: T[], query: string) => T[];
  onPick: (item: T) => void;
  isSelected?: (item: T) => boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [search, setSearchState] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const open = anchor !== null;

  const filtered = useMemo(() => filter(items, search), [filter, items, search]);

  const close = () => {
    setAnchor(null);
    setSearchState('');
  };

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setActiveIndex(isSelected ? Math.max(0, items.findIndex(isSelected)) : 0);
    setAnchor(rect);
    window.requestAnimationFrame(() => searchRef.current?.focus());
  };

  const toggle = () => (open ? close() : openMenu());

  const pick = (item: T) => {
    onPick(item);
    close();
  };

  const setSearch = (value: string) => {
    setSearchState(value);
    setActiveIndex(0);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    const onReposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setAnchor(rect);
      else close();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      window.addEventListener('resize', onReposition);
      window.addEventListener('scroll', onReposition, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current
      ?.querySelector<HTMLElement>(`[data-at-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = filtered.length;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }
    if (count === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(i => (i + 1) % count);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + count) % count);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = filtered[activeIndex];
      if (item) pick(item);
    }
  };

  return {
    open,
    anchor,
    triggerRef,
    menuRef,
    searchRef,
    search,
    setSearch,
    filtered,
    activeIndex,
    setActiveIndex,
    toggle,
    pick,
    onSearchKeyDown
  };
}
